import React, { Suspense, lazy, useState } from 'react';
import ThemeToggle from "./ThemeToggle";

const MachineSelector = lazy(() => import("./MachineSelector"));
const HMIHomeScreen = lazy(() => import("./HMIHomeScreen"));
const HMIDashboard = lazy(() => import("./HMIDashboard"));
const PressSafetyScreen = lazy(() => import("./ProductionDataScreen"));
const HourlyBatchScreen = lazy(() => import("./HourlyDieHeight"));
const AnalyticsDashboardScreen = lazy(() => import("./AnalyticsDashboardScreen"));
const IV3AnalyticsScreen = lazy(() => import("./IV3AnalyticsScreen"));
const DieHeightControlScreen = lazy(() => import("./DieHeightControlScreen"));
const IV3Dashboard = lazy(() => import("./iv3/IV3Dashboard"));
const ConditionMonitoringScreen = lazy(() => import("./ConditionMonitoringScreen"));

function HMIMainApp() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [currentScreen, setCurrentScreen] = useState("plant");
  const [selectedPlant, setSelectedPlant] = useState(null);

  const loadingScreen = (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#171d2b",
        color: "#e5e7eb",
        fontFamily: "Arial, sans-serif",
        fontSize: "16px",
        fontWeight: 600,
      }}
    >
      Loading screen...
    </div>
  );

  const handleNavigateToHourlyBatch = (date) => {
    setSelectedDate(date);
    setCurrentScreen("hourlyBatch");
  };

  const handleBackToSelector = () => {
    if (
      currentScreen !== "plant" &&
      currentScreen !== "machineList" &&
      currentScreen !== "home"
    ) {
      setCurrentScreen("home");
      return;
    }

    if (currentScreen === "home") {
      setSelectedMachine(null);
      setCurrentScreen("machineList");
      return;
    }

    if (currentScreen === "machineList") {
      setSelectedPlant(null);
      setCurrentScreen("plant");
    }
  };

  const handleBackToHome = () => {
    setCurrentScreen('home');
  };

  const handleNavigateToActiveAlarms = () => {
    setCurrentScreen('activeAlarm');
  };

  const handleNavigateToHistory = () => {
    setCurrentScreen('alarmHistory');
  };

  const handleNavigateToPressSafety = () => {
    setCurrentScreen('pressSafety');
  };

  const handleNavigateToDieHeightControl = () => {
    setCurrentScreen("dieHeightControl");
  };

  const handleNavigateToIV3 = () => {
    setCurrentScreen("iv3");
  };

  const handleNavigateToConditionMonitoring = () => {
    setCurrentScreen("conditionMonitoring");
  };

  const handleNavigateToAnalytics = () => {
    setCurrentScreen("analytics");
  };

  const handleNavigateToIV3Analytics = () => {
    setCurrentScreen("iv3Analytics");
  };

  const handleBackToPlantSelection = () => {
    setSelectedMachine(null);
    setSelectedPlant(null);
    setCurrentScreen("plant");
  };

  const renderScreen = () => {
    switch (currentScreen) {
      case 'home':
        return (
          <HMIHomeScreen
            machineNumber={selectedMachine}
            selectedPlant={selectedPlant}
            onBackToSelector={handleBackToSelector}
            onBackGoHome={handleBackToPlantSelection}
            onNavigateToActiveAlarms={handleNavigateToActiveAlarms}
            onNavigateToHistory={handleNavigateToHistory}
            onNavigateToPressSafety={handleNavigateToPressSafety}
            onNavigateToDieHeightControl={handleNavigateToDieHeightControl}
            onNavigateToIV3={handleNavigateToIV3}
            onNavigateToConditionMonitoring={handleNavigateToConditionMonitoring}
            onNavigateToHourlyBatch={handleNavigateToHourlyBatch}
            onSelectMachineDirect={(machine) => setSelectedMachine(machine)}
          />
        );
      case "hourlyBatch":
        return (
          <HourlyBatchScreen
            machineNumber={selectedMachine}
            date={selectedDate}
            onBackToSelector={handleBackToSelector}
            onNavigateToActiveAlarms={handleNavigateToActiveAlarms}
            onNavigateToHistory={handleNavigateToHistory}
            onBackToHome={handleBackToHome}
          />
        );
      case 'activeAlarm':
        return (
          <HMIDashboard
            machineNumber={selectedMachine}
            onBackToHome={handleBackToHome}
            onBackToSelector={handleBackToSelector}
            isActiveAlarm={true}
            onSwitchToActive={handleNavigateToActiveAlarms}
            onSwitchToHistory={handleNavigateToHistory}
          />
        );
      case 'alarmHistory':
        return (
          <HMIDashboard
            machineNumber={selectedMachine}
            onBackToHome={handleBackToHome}
            onBackToSelector={handleBackToSelector}
            isActiveAlarm={false}
            onSwitchToActive={handleNavigateToActiveAlarms}
            onSwitchToHistory={handleNavigateToHistory}
          />
        );
      case 'pressSafety':
        return (
          <PressSafetyScreen
            machineNumber={selectedMachine}
            onBackToHome={handleBackToHome}
            onBackToSelector={handleBackToSelector}
          />
        );
      case "dieHeightControl":
        return (
          <DieHeightControlScreen
            machineNumber={selectedMachine}
            onBackToHome={handleBackToHome}
            onBackToSelector={handleBackToSelector}
          />
        );
      case "analytics":
        return (
          <AnalyticsDashboardScreen
            onBackToSelector={handleBackToPlantSelection}
          />
        );
      case "iv3Analytics":
        return (
          <IV3AnalyticsScreen
            onBackToSelector={handleBackToPlantSelection}
          />
        );
      case "iv3":
        return (
          <IV3Dashboard
            machineNumber={selectedMachine}
            onBackToHome={handleBackToHome}
            onBackToSelector={handleBackToSelector}
          />
        );
      case "conditionMonitoring":
        return (
          <ConditionMonitoringScreen
            machineNumber={selectedMachine}
            onBackToHome={handleBackToHome}
            onBackToSelector={handleBackToSelector}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Suspense fallback={loadingScreen}>
      <div className="App">
        <ThemeToggle />
        {(currentScreen === "plant" || currentScreen === "machineList") && (
          <MachineSelector
            selectedPlant={selectedPlant}
            setSelectedPlant={setSelectedPlant}
            onOpenAnalytics={handleNavigateToAnalytics}
            onOpenIV3Analytics={handleNavigateToIV3Analytics}
            onSelectMachine={(machine) => {
              setSelectedMachine(machine);
              setCurrentScreen("home");
            }}
          />
        )}

        {currentScreen !== "plant" &&
          currentScreen !== "machineList" &&
          renderScreen()}
      </div>
    </Suspense>
  );
}

export default HMIMainApp;
